import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { getBusiness } from "services/businessService";
import { Business } from "types";
import Card from "components/card";
import { useToast } from "components/Toast";
import { useAuth } from "hooks/useAuth";
import Button from "components/button";

const BusinessShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { user } = useAuth();

  const fetchBusiness = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getBusiness(parseInt(id));
      setBusiness(data);
    } catch (err: any) {
      console.error(err);
      toast.showError(
        err.response?.data?.message || "Failed to load business details."
      );
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  if (loading) {
    return (
      <Card extra="w-full p-4">
        <p>Loading business details...</p>
      </Card>
    );
  }

  if (!business) {
    return (
      <Card extra="w-full p-4">
        <p>Business not found.</p>
      </Card>
    );
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    }
  };

  return (
    <div className="flex w-full flex-col gap-5 lg:gap-5">
      <div className="w-ful mt-3 flex h-fit flex-col gap-5 lg:grid lg:grid-cols-12">
        <div className="col-span-12 lg:col-span-12 lg:mb-0">
          <Card extra="w-full h-full p-4">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h1 className="mb-2 text-3xl font-bold text-navy-700 dark:text-white">
                  {business.name}
                </h1>
                <p className="mb-4 text-lg text-gray-700 dark:text-gray-200">
                  Category: {business.category}
                </p>
              </div>
              <span
                className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeColor(
                  business.status
                )}`}
              >
                {business.status.charAt(0).toUpperCase() +
                  business.status.slice(1)}
              </span>
            </div>
            <p className="mb-6 text-gray-600 dark:text-gray-300">
              {business.description}
            </p>

            <div className="mb-4">
              <h2 className="mb-2 text-xl font-semibold text-navy-700 dark:text-white">
                Contact Information
              </h2>
              {business.phone && (
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Phone:</strong> {business.phone}
                </p>
              )}
              {business.email && (
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Email:</strong> {business.email}
                </p>
              )}
              {business.website && (
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Website:</strong>{" "}
                  <a
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-500 hover:underline dark:text-brand-400"
                  >
                    {business.website}
                  </a>
                </p>
              )}
              {business.address && (
                <p className="text-gray-600 dark:text-gray-300">
                  <strong>Address:</strong> {business.address}
                </p>
              )}
            </div>

            <div className="mb-4">
              <h2 className="mb-2 text-xl font-semibold text-navy-700 dark:text-white">
                Owner Information
              </h2>
              {business.user && (
                <>
                  <p className="text-gray-600 dark:text-gray-300">
                    <strong>Name:</strong> {business.user.firstname}{" "}
                    {business.user.lastname}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    <strong>Village:</strong> {business.user.village}
                  </p>
                </>
              )}
            </div>

            <div className="flex gap-3">
              <Link to="/user/businesses">
                <Button variant="outline">Back to Businesses</Button>
              </Link>
              {user && business.user.id === user.id && (
                <Link to={`/user/business/edit`}>
                  <Button variant="primary">Edit Business</Button>
                </Link>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BusinessShow;
